import PubNub from 'pubnub';
import { io } from 'socket.io-client';
import React from 'react';

import { PARTNER_TOKEN_KEY, PARTNER_USER_KEY } from 'utils/sessionCoordinator';

// Environment variables
// `VITE_APP_USE_PUBNUB` comes through as a string ("true"/"false") — coerce explicitly.
const USE_PUBNUB = String(import.meta.env.VITE_APP_USE_PUBNUB ?? 'true').toLowerCase() !== 'false';
const PUBNUB_SUBSCRIBE_KEY = import.meta.env.VITE_APP_PUBNUB_SUBSCRIBE_KEY;
const SOCKET_SERVER_URL = import.meta.env.VITE_APP_SOCKET_SERVER_URL || 'https://dev-api.northalley.com';

export default interface BaseModel {
    id: string;
    typename: string;
    eventStatus: null | string;
    eventResult: null | string;
    updatedBy: null | string;
    revisionId: number;
    updatedAt: string;
    createdAt: string;
}

type EventListener = (data: BaseModel) => void;
type ChannelKey = string | number;

export default class Sockets {
    private _userId: string;
    private _updateModelAttributes: any;
    private _pubnub: PubNub | null = null;
    private _socket: any = null;
    private _pubnubListener: any = null;

    // channel key -> set of listeners for that channel/event id
    private _eventListeners: Map<string, Set<EventListener>> = new Map();
    // channel key -> ms timestamp of first subscribe (used to drop stale/replayed events)
    private _subscribedAt: Map<string, number> = new Map();
    // channels currently subscribed on the transport (guards against duplicate subs)
    private _subscribedChannels: Set<string> = new Set();

    static instance: any;

    constructor(userId: string, updateModelAttributes: any) {
        this._userId = userId;
        this._updateModelAttributes = updateModelAttributes;

        if (USE_PUBNUB) {
            this._pubnub = new PubNub({
                subscribeKey: PUBNUB_SUBSCRIBE_KEY,
                userId: this._userId,
                logVerbosity: false,
                restore: false
            });

            this._pubnubListener = {
                status: (statusEvent: any) => {
                    // Surface PubNub connection lifecycle so connectivity issues are visible.
                    if (statusEvent.category === 'PNConnectedCategory') {
                        // eslint-disable-next-line no-console
                        console.log('[Sockets] PubNub connected', { channels: statusEvent.affectedChannels ?? statusEvent.subscribedChannels });
                    } else if (
                        statusEvent.error ||
                        [
                            'PNNetworkIssuesCategory',
                            'PNNetworkDownCategory',
                            'PNAccessDeniedCategory',
                            'PNBadRequestCategory',
                            'PNTimeoutCategory',
                            'PNUnknownCategory'
                        ].includes(statusEvent.category)
                    ) {
                        // eslint-disable-next-line no-console
                        console.error('[Sockets] PubNub status error:', statusEvent.category, statusEvent);
                    }
                },
                message: (messageEvent: any) => this.handleMessageEvent(messageEvent.message, messageEvent.channel, messageEvent.timetoken),
                presence: () => {}
            };
            this._pubnub.addListener(this._pubnubListener);

            this._subscribeChannel(this._userId);
        } else {
            this._socket = io(SOCKET_SERVER_URL, {
                path: '/soc-api/socket.io'
            });

            this._socket.on('connect', () => {
                this._socket.emit('join', this._userId);
                this._socket.emit('registerCustomer', { id: this._userId });
            });

            this._socket.on('message', (msg: any) => this.handleMessageEvent(msg));
        }
    }

    private _toKey(channel: ChannelKey | null | undefined): string {
        return channel === null || channel === undefined ? '' : String(channel);
    }

    private _subscribeChannel(channel: string) {
        if (!channel || this._subscribedChannels.has(channel)) return;
        this._subscribedChannels.add(channel);
        if (USE_PUBNUB && this._pubnub) {
            this._pubnub.subscribe({ channels: [channel] });
        } else if (this._socket) {
            this._socket.emit('join', channel);
            this._socket.emit('registerEvent', { id: channel });
        }
    }

    private _unsubscribeChannel(channel: string) {
        if (!channel || !this._subscribedChannels.has(channel)) return;
        this._subscribedChannels.delete(channel);
        if (USE_PUBNUB && this._pubnub) {
            this._pubnub.unsubscribe({ channels: [channel] });
        } else if (this._socket) {
            this._socket.emit('leave', channel);
        }
    }

    private _messageTimestamp(message: any, pubnubTimetoken?: string | number): number {
        // Prefer PubNub's server-assigned timetoken — it's always present and
        // represents the moment the message was published (17-digit, 100-ns units).
        if (pubnubTimetoken !== undefined && pubnubTimetoken !== null && pubnubTimetoken !== '') {
            const tt = typeof pubnubTimetoken === 'number' ? pubnubTimetoken : Number(pubnubTimetoken);
            if (!isNaN(tt) && tt > 0) return Math.floor(tt / 10000); // -> ms
        }
        const t = message?.updatedAt ?? message?.createdAt ?? message?.timestamp;
        if (t === undefined || t === null) return Date.now();
        if (typeof t === 'number') return t;
        const parsed = Date.parse(String(t));
        return isNaN(parsed) ? Date.now() : parsed;
    }

    // CENTRAL MESSAGE HANDLER
    private handleMessageEvent(message: any, channel?: string, pubnubTimetoken?: string | number) {
        const updatedModelAttributes = message;
        if (!updatedModelAttributes) return;

        // system-level handling
        if (updatedModelAttributes.status === 'forcelogout') {
            const tokenValue = localStorage.getItem('serviceToken');
            if (updatedModelAttributes.accessToken !== tokenValue) {
                localStorage.removeItem('serviceToken');
                window.location.pathname = '/devicelimit';
                return;
            }
        }

        if (updatedModelAttributes.status === 'forcelogoutFromMp') {
            const oldtokenValue = localStorage.getItem(PARTNER_TOKEN_KEY);
            const partenerUser = JSON.parse(localStorage.getItem(PARTNER_USER_KEY) || '{}');

            if (updatedModelAttributes.curentToken !== oldtokenValue) {
                localStorage.removeItem(PARTNER_TOKEN_KEY);
                localStorage.removeItem(PARTNER_USER_KEY);
                localStorage.setItem(
                    'mp_mpidMessage',
                    'Your session has been terminated because your account was accessed from another device. If you do not recognize this activity, please contact your superior immediately.'
                );
                window.location.pathname = `/mp/${partenerUser.mpId}`;
                return;
            }
        }

        if (updatedModelAttributes.status === 'maintenance') {
            localStorage.removeItem('serviceToken');
            window.location.pathname = '/maintenance';
            return;
        }

        if (updatedModelAttributes.status === 'suspend') {
            localStorage.removeItem('serviceToken');
            location.reload();
            return;
        }

        if (!updatedModelAttributes.id || !updatedModelAttributes.typename) return;

        // Drop messages whose server-publish time is older than when we (re)subscribed.
        // This kills PubNub replays of events that happened while the page was unmounted.
        const msgTs = this._messageTimestamp(updatedModelAttributes, pubnubTimetoken);
        const chanKeyEarly = this._toKey(channel);
        const idKeyEarly = this._toKey(updatedModelAttributes.id);
        const earliestSub = Math.min(this._subscribedAt.get(chanKeyEarly) ?? Infinity, this._subscribedAt.get(idKeyEarly) ?? Infinity);
        if (earliestSub !== Infinity && msgTs < earliestSub) return;

        // global model update (redux / toasts)
        this._updateModelAttributes?.(updatedModelAttributes);

        // event-specific listeners: dispatch by the channel the message arrived on,
        // and by message id — normalized to string, de-duplicated so no listener
        // fires twice for one message. The global handler above is also added to
        // `fired` so a page that passed it as a per-event listener doesn't double-run.
        const keys: string[] = [];
        const chanKey = this._toKey(channel);
        if (chanKey) keys.push(chanKey);
        const idKey = this._toKey(updatedModelAttributes.id);
        if (idKey && !keys.includes(idKey)) keys.push(idKey);

        const fired = new Set<EventListener>();
        if (typeof this._updateModelAttributes === 'function') {
            fired.add(this._updateModelAttributes);
        }
        keys.forEach((key) => {
            const subAt = this._subscribedAt.get(key);
            // drop events produced before we started listening on this channel
            if (subAt !== undefined && msgTs < subAt) return;
            const listeners = this._eventListeners.get(key);
            if (!listeners || listeners.size === 0) return;
            Array.from(listeners).forEach((cb) => {
                if (fired.has(cb)) return;
                fired.add(cb);
                try {
                    cb(updatedModelAttributes as BaseModel);
                } catch (e) {
                    console.error('[Sockets] listener error', e);
                }
            });
        });
    }

    // Subscribe a channel without attaching a listener (e.g. secondary race channel).
    subscribeRaceEventId(eventId: ChannelKey) {
        const key = this._toKey(eventId);
        if (!key) return;
        if (!this._subscribedAt.has(key)) {
            this._subscribedAt.set(key, Date.now());
        }
        this._subscribeChannel(key);
    }

    // Subscribe a listener for a given event id / channel. Safe to call multiple
    // times — the transport subscription is only created once, and the listener
    // set is de-duplicated by reference.
    subscribeEventId(eventId: ChannelKey, listener: EventListener) {
        const key = this._toKey(eventId);
        if (!key || typeof listener !== 'function') return;

        if (!this._eventListeners.has(key)) {
            this._eventListeners.set(key, new Set());
        }
        if (!this._subscribedAt.has(key)) {
            this._subscribedAt.set(key, Date.now());
        }
        this._subscribeChannel(key);

        this._eventListeners.get(key)!.add(listener);
    }

    // Remove a single listener (or all listeners for the channel). When the last
    // listener leaves, the transport channel is also released.
    unsubscribeEventId(eventId: ChannelKey, listener?: EventListener) {
        const key = this._toKey(eventId);
        if (!key) return;

        const listeners = this._eventListeners.get(key);
        if (listeners) {
            if (listener) {
                listeners.delete(listener);
            } else {
                listeners.clear();
            }
            if (listeners.size > 0) return;
            this._eventListeners.delete(key);
        }

        this._subscribedAt.delete(key);
        // Never drop the user's own channel — it carries system events (forcelogout, wallet, etc.)
        if (key !== this._userId) {
            this._unsubscribeChannel(key);
        }
    }

    unsubscribe(channels: null | Array<ChannelKey> = null) {
        if (channels && channels.length) {
            channels.forEach((c) => this.unsubscribeEventId(c));
            return;
        }
        // full teardown (logout) — release this instance so the next login
        // builds a fresh one with the new user's id.
        this._eventListeners.clear();
        this._subscribedAt.clear();
        this._subscribedChannels.clear();
        if (USE_PUBNUB && this._pubnub) {
            if (this._pubnubListener) {
                this._pubnub.removeListener(this._pubnubListener);
                this._pubnubListener = null;
            }
            this._pubnub.unsubscribeAll();
            this._pubnub = null;
        } else if (this._socket) {
            this._socket.disconnect();
            this._socket = null;
        }
        if (Sockets.instance === this) {
            Sockets.instance = null;
        }
    }

    static getCurrentSocket() {
        return this.instance;
    }

    static setSocketForUser(userId: string, setModelFunction: any) {
        if (!this.instance) {
            this.instance = new Sockets(userId, setModelFunction);
        }
        return this.instance;
    }
}
